<script lang="ts">
  import { router } from "@inertiajs/svelte";
  import { usersApi, type UserSummary } from "../../lib/api";
  import Modal from "./Modal.svelte";

  let {
    show,
    onClose,
  }: {
    show: boolean;
    onClose: () => void;
  } = $props();

  let searchQuery = $state("");
  let searchResults: UserSummary[] = $state([]);
  let searching = $state(false);
  let searchTimeout: ReturnType<typeof setTimeout>;

  function handleSearch() {
    clearTimeout(searchTimeout);
    if (searchQuery.trim().length < 2) {
      searchResults = [];
      return;
    }

    searchTimeout = setTimeout(async () => {
      searching = true;
      try {
        const result = await usersApi.search(searchQuery);
        if (result.success) {
          searchResults = result.data || [];
        }
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        searching = false;
      }
    }, 300);
  }

  function goToProfile(userId: string) {
    handleClose();
    router.visit(`/users/${userId}`);
  }

  function handleClose() {
    searchQuery = "";
    searchResults = [];
    onClose();
  }
</script>

<Modal show={show} title="사용자 검색" onClose={handleClose}>
  <div class="space-y-4">
    <div class="relative">
      <input
        type="text"
        bind:value={searchQuery}
        oninput={handleSearch}
        class="input pl-10"
        placeholder="이름 또는 닉네임으로 검색..."
      />
      <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--text-muted);">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      {#if searching}
        <svg class="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24" style="color: var(--text-muted);">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      {/if}
    </div>

    <!-- Search Results -->
    {#if searchResults.length > 0}
      <div class="max-h-80 overflow-y-auto space-y-1">
        {#each searchResults as result}
          <button
            onclick={() => goToProfile(result.id)}
            class="w-full flex items-center gap-3 p-3 rounded-xl transition-colors duration-150 text-left cursor-pointer"
            style="color: var(--text-primary);"
            onmouseenter={(e) => (e.currentTarget as HTMLElement).style.background = 'var(--surface-hover)'}
            onmouseleave={(e) => (e.currentTarget as HTMLElement).style.background = 'transparent'}
          >
            {#if result.avatar_url}
              <img src={result.avatar_url} alt={result.name} class="w-11 h-11 rounded-full object-cover" />
            {:else}
              <div class="w-11 h-11 rounded-full flex items-center justify-center font-semibold text-base" style="background: var(--accent-dim); color: var(--accent);">
                {result.name.charAt(0)}
              </div>
            {/if}
            <div>
              <div class="font-medium text-sm">{result.name}</div>
              {#if result.nickname}
                <div class="text-xs mt-0.5" style="color: var(--text-muted);">@{result.nickname}</div>
              {/if}
            </div>
          </button>
        {/each}
      </div>
    {:else if searchQuery.length >= 2 && !searching}
      <div class="text-center py-8" style="color: var(--text-secondary);">
        <svg class="w-12 h-12 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--text-muted);">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <p class="text-sm">검색 결과가 없습니다</p>
        <p class="text-xs mt-1" style="color: var(--text-muted);">다른 이름이나 닉네임으로 검색해보세요</p>
      </div>
    {:else if searchQuery.length < 2}
      <div class="text-center py-8" style="color: var(--text-secondary);">
        <svg class="w-12 h-12 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--text-muted);">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <p class="text-sm">함께 여행할 친구를 찾아보세요</p>
        <p class="text-xs mt-1" style="color: var(--text-muted);">2글자 이상 입력하세요</p>
      </div>
    {/if}
  </div>
</Modal>
